import './AdrCalculator.css'
import { Helmet } from 'react-helmet-async';
import ArrowRight from '../../icons/ArrowRight';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useLanguagePrefix from '../../LanguagePrefix';
import ToolFixedMenu from './ToolFixedMenu';

function AdrCalculator() {
    const langPrefix = useLanguagePrefix();
    const [roomRevenue, setRoomRevenue] = useState(null);
    const [roomSold, setRoomSold] = useState(null);
    const [adr, setAdr] = useState(null);
    const { t } = useTranslation();
    const fixedMenuRef = React.useRef(null);

    useEffect(() => {
        const menu = fixedMenuRef.current;
        const footer = document.getElementById('main-footer');
        if (!menu || !footer) return;

        const isAbsolute = { current: false };

        const handleScroll = () => {
            const footerRect = footer.getBoundingClientRect();

            // Compute where the menu's bottom would be if fixed
            const scrollY = window.scrollY || window.pageYOffset;
            const fixedMenuTop = scrollY + 40;
            const fixedMenuBottom = fixedMenuTop + menu.offsetHeight;

            // Compute where the footer's top is relative to document
            const footerTopAbsolute = scrollY + footerRect.top;

            const distance = footerTopAbsolute - fixedMenuBottom;

            if (!isAbsolute.current && distance < 40) {
                // Enter absolute mode
                isAbsolute.current = true;
                menu.style.position = 'absolute';
                menu.style.top = `${footerTopAbsolute - menu.offsetHeight - 40}px`;
                menu.style.bottom = 'auto';
                menu.style.right = '20px';
            } else if (isAbsolute.current && distance >= 40) {
                // Switch back to fixed
                isAbsolute.current = false;
                menu.style.position = 'fixed';
                menu.style.top = '90px';
                menu.style.bottom = 'auto';
                menu.style.right = '20px';
            }
        };

        window.addEventListener('scroll', handleScroll);
        window.addEventListener('resize', handleScroll);
        handleScroll();

        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleScroll);
        };
    }, []);


    const handleInputChange = (e, type) => {
        const value = e.target.value;
        if (type === 'revenue') {
            setRoomRevenue(value);
        } else if (type === 'sold') {
            setRoomSold(value);
        }
    };

    const calculateADR = () => {
        if (roomRevenue > 0 && roomSold > 0) {
            const result = roomRevenue / roomSold;
            const formatted = Number.isInteger(result) ? result : result.toFixed(2);
            setAdr(formatted);

        } else {
            setAdr(null);
        }
    }

    return (
        <div>
            <Helmet> 

                <title>ADR Calculator – Average Daily Rate for Hotels - Prostay</title>
                <meta name="description" content="Use Prostay’s free ADR calculator to measure your hotel’s average daily rate. Analyze pricing strategies and improve revenue per room." />
                <link rel="canonical" href={"https://www.prostay.com" + langPrefix + "/adr-calculator/"} />

                <link rel="alternate" href="https://www.prostay.com/en/adr-calculator/" />
                <link rel="alternate" href="https://www.prostay.com/es/adr-calculator/" />

                <meta property="og:title" content="ADR Calculator – Average Daily Rate for Hotels - Prostay" />
                <meta property="og:description" content="Use Prostay’s free ADR calculator to measure your hotel’s average daily rate. Analyze pricing strategies and improve revenue per room." />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={"https://www.prostay.com" + langPrefix + "/adr-calculator/"} />
                <meta property="og:image" content='https://prostay.com/images/ogImage.png' />

                <meta property="og:site_name" content="Prostay" />

                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="ADR Calculator – Average Daily Rate for Hotels - Prostay" />
                <meta name="twitter:description" content="Use Prostay’s free ADR calculator to measure your hotel’s average daily rate. Analyze pricing strategies and improve revenue per room." />
                <meta name="twitter:image" content='https://prostay.com/images/ogImage.png' />
                <meta name="twitter:site" content="@prostaypms" />
                <meta name="twitter:creator" content="@prostaypms" />



            </Helmet>
            <div className='d-flex adr-calculator flex-column-reverse flex-xl-row'>
                <div className='d-flex flex-column width-left'>
                    <div>
                        <h1 style={{ margin: '0px' }}>
                            {t('ADR Calculator - Average Daily Rate for Hotels')}
                        </h1>
                        <h6 className='mt-4'>
                            {t('Use this free ADR calculator from Prostay to quickly determine your hotel’s average daily rate.')} <br />
                            {t('ADR is one of the most important metrics in hotel revenue management, helping you evaluate pricing performance and benchmark your property against competitors.')}
                        </h6>
                        <div className='d-flex flex-column-reverse flex-xl-row gap-2 gap-sm-5 mt-5'>
                            <div className='d-flex flex-column gap-4'>
                                <div className='d-flex flex-column'>
                                    <label>
                                        {t('Total Room Revenue')}
                                    </label>
                                    <input
                                        type='number'
                                        value={roomRevenue}
                                        onChange={(e) => handleInputChange(e, 'revenue')}
                                        placeholder={t('Enter amount here')} />
                                </div>
                                <div className='d-flex flex-column mt-4'>
                                    <label>
                                        {t('Total Room Sold')}
                                    </label>
                                    <input
                                        type='number'
                                        value={roomSold}
                                        onChange={(e) => handleInputChange(e, 'sold')}
                                        placeholder={t('Enter number here')} />
                                </div>
                                <div className='d-flex gap-3 align-items-center flex-column flex-sm-row gap-sm-5'>
                                    <button className='light-blue-filled-btn-xsmall' style={{ height: '38px', width: '111px' }} onClick={calculateADR}>
                                        {t('Calculate')}
                                        <ArrowRight />
                                    </button>
                                    <div className='d-flex flex-column w-100'>
                                        <label>
                                            {t('Average Daily Rate')}
                                        </label>
                                        <input
                                            readOnly
                                            value={adr}
                                            type='number'
                                            placeholder='0' />
                                    </div>
                                </div>
                            </div>
                            <div className='center-div'>
                                <h2>
                                    {t('What is ADR in Hotels?')}
                                </h2>
                                <p className='mt-3'>
                                    {t('Average Daily Rate (ADR) is a fundamental hotel performance metric that shows the average revenue earned per sold room during a specific time period. It is used by hoteliers, revenue managers, and investors to assess pricing strategy, profitability, and overall success.')}
                                </p>
                                <br />
                                <p>
                                    {t('ADR is often analyzed alongside RevPAR (Revenue per Available Room) and Occupancy Rate to build a complete view of hotel performance.')}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className='gap-3 d-flex flex-column mt-5'>
                        <h2>
                            {t('ADR Formula')}
                        </h2>
                        <p>
                            {t('The formula for calculating ADR is: Total revenue from rooms divided by total number of rooms sold.')}
                        </p>
                        <div className='d-flex align-items-center formula gap-1'>
                            <p>
                                {t('ADR')} =
                            </p>
                            <div className='d-flex flex-column align-items-center'>
                                <p>
                                    {t('Total Room Revenue')}
                                </p>
                                <p style={{ borderTop: '0.5px solid #1643E0' }}>
                                    {t('Number of Rooms Sold')}
                                </p>
                            </div>
                        </div>
                        <ul style={{ maxWidth: '500px' }}>
                            <li>
                                <p>
                                    {t('Total Room Revenue: Only revenue from sold rooms (exclude food & beverage, spa, or other departments)')}
                                </p>
                            </li>
                            <li>
                                <p>
                                    {t('Number of Rooms Sold: Total rooms sold, excluding complimentary rooms and out-of-order rooms')}
                                </p>
                            </li>
                        </ul>
                    </div>
                    <div className='d-flex flex-column gap-3 mt-3'>
                        <h2>
                            {t('Why ADR Matters for Hotels')}
                        </h2>
                        <p>
                            {t('Tracking ADR is essential for any hotel or resort focused on profitability. A strong ADR indicates that your pricing strategy is working — you’re not just filling rooms, but filling them at rates that support your business goals.')}
                        </p>
                        <p>
                            {t('ADR also plays a critical role in your broader revenue management strategy. When combined with other key performance indicators like Occupancy Rate and RevPAR, ADR provides a full picture of your hotel’s health. For example, if your ADR is high but occupancy is low, you may be overpricing rooms. If ADR is low and occupancy is high, you might be underpricing and leaving money on the table.')}
                        </p>
                        <p>
                            {t('Beyond internal analysis, ADR is also useful for benchmarking. You can compare your property’s ADR against similar hotels in your destination, star category, or competitive set. This helps you identify opportunities to reposition your pricing in line with market trends.')}
                        </p>
                    </div>
                    <div className='d-flex flex-column gap-3 mt-4'>
                        <h2>
                            {t('Understanding the Advantages of ADR and RevPAR')}
                        </h2>
                        <p>
                            {t('Average Daily Rate (ADR) is a critical metric in hotel revenue management because it tells you how much revenue you earn per sold room. It allows hoteliers to:')}
                        </p>
                        <ul>
                            <li>
                                <p>
                                    {t('Evaluate pricing strategy: By tracking ADR over time, you can see if your rates are increasing or decreasing and adjust accordingly.')}
                                </p>
                            </li>
                            <li>
                                <p>
                                    {t('Monitor rate trends: ADR helps you understand seasonal demand patterns and adapt your rates dynamically.')}
                                </p>
                            </li>
                            <li>
                                <p>
                                    {t('Benchmark performance: You can compare ADR with similar properties in your market or segment.')}
                                </p>
                            </li>
                            <li>
                                <p>
                                    {t('Target high-yield guests: It shows which channels, segments, or packages generate the highest-paying bookings.')}
                                </p>
                            </li>
                        </ul>
                        <p>
                            {t('While ADR is powerful, it only tells part of the story. ADR doesn’t take into account how many rooms were actually sold out of your total inventory.')}
                        </p>
                        <div>
                            <p>
                                {t('This means:')}
                            </p>
                            <ul>
                                <li>
                                    <p>
                                        {t('A hotel could have a high ADR but low occupancy, signaling inefficiency.')}
                                    </p>
                                </li>
                                <li>
                                    <p>
                                        {t('It can’t show lost revenue from unsold rooms.')}
                                    </p>
                                </li>
                                <li>
                                    <p>
                                        {t('ADR can be misleading when compared across properties with different occupancy levels.')}
                                    </p>
                                </li>
                            </ul></div>
                    </div>
                    <div className='d-flex flex-column gap-3 mt-3'>
                        <h2>
                            {t('Why RevPAR Complements ADR')}
                        </h2>
                        <p>
                            {t('Revenue Per Available Room (RevPAR) solves the blind spots of ADR by factoring in both pricing and occupancy. It gives you a more complete view of your hotel’s overall room revenue performance.')}
                        </p>
                        <div className='d-flex align-items-center formula gap-1 flex-column flex-sm-row'>
                            <div className='d-flex align-items-center gap-1'>
                                <p>
                                    {t('RevPAR')} =
                                </p>
                                <div className='d-flex flex-column align-items-center'>
                                    <p style={{ borderBottom: '0.5px solid #1643E0' }}>
                                        {t('Total Room Revenue')}
                                    </p>
                                    <p>
                                        {t('Available Rooms')}
                                    </p>
                                </div>
                            </div>
                            <p style={{ paddingInline: '25px' }}>
                                {t('or')}
                            </p>
                            <p>
                                {t('RevPAR = ADR x Occupancy Rate')}
                            </p>
                        </div>
                        <div>
                            <p>
                                {t('Benefits of RevPAR:')}
                            </p>
                            <ul>
                                <li>
                                    <p>
                                        {t('Measures revenue efficiency across all available rooms, not just those sold.')}
                                    </p>
                                </li>
                                <li>
                                    <p>
                                        {t('Highlights demand issues: A drop in RevPAR could mean falling occupancy even if ADR stays strong.')}
                                    </p>
                                </li>
                                <li>
                                    <p>
                                        {t('Balances occupancy and rate strategy: Encourages a more strategic approach, ensuring that you aren’t sacrificing one for the other.')}
                                    </p>
                                </li>
                                <li>
                                    <p>
                                        {t('Ideal for revenue forecasting and budgeting: Especially for revenue managers, RevPAR is the go-to metric.')}
                                    </p>
                                </li>
                            </ul>
                        </div>
                        <p>
                            {t('To truly optimize revenue, you should track both ADR and RevPAR side by side. This dual approach ensures you’re not only charging the right price but also maximizing your overall earning potential from available rooms.')}
                        </p>
                        <p>
                            {t('That’s where the Prostay')} <a style={{ color: '#1643E0', cursor: 'pointer' }} href={`${langPrefix}/property-management-system/`}>{t('Property Management System')}</a> {t('comes into play with detailed easy to read reports updated in real time to save you time from making calculations so you can focus more on making the right decisions.')}
                        </p>
                    </div>
                </div>
                <ToolFixedMenu />
            </div>
        </div>
    )
}

export default AdrCalculator;